import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { onAuthStateChanged, signOut, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from './firebase';

import Dashboard from './pages/Dashboard';
import LinkDetails from './pages/LinkDetails';
import LoginScreen from './pages/LoginScreen';
import RedirectHandler from './pages/RedirectHandler';
import AdminReport from './pages/AdminReport';
import AuditLog from './pages/AuditLog';
import { ToastProvider } from './components/ToastProvider';
import { LoadingState } from './components/States';

const useGoogleFont = (fontFamily) => {
    useEffect(() => {
        const head = document.head;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(' ', '+')}:wght@400;500;600;700&display=swap`;
        head.appendChild(link);

        const style = document.createElement('style');
        style.innerHTML = `body { font-family: '${fontFamily}', sans-serif; }`;
        head.appendChild(style);

        return () => {
            head.removeChild(link);
            head.removeChild(style);
        };
    }, [fontFamily]);
};

const useFavicon = (href) => {
    useEffect(() => {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }
        link.type = 'image/png';
        link.href = href;
    }, [href]);
};

const AppManager = ({ children }) => {
    const location = useLocation();

    useEffect(() => {
        const path = location.pathname;
        if (path === '/login') {
            document.title = 'เข้าสู่ระบบ - RMUTTO URL Shortener';
        } else if (path.startsWith('/details/')) {
            document.title = 'รายละเอียดลิงก์ - RMUTTO URL Shortener';
        } else if (path === '/') {
            document.title = 'แดชบอร์ด - RMUTTO URL Shortener';
        } else if (path === '/admin-report') {
            document.title = 'รายงานภาพรวม - RMUTTO URL Shortener';
        } else if (path === '/audit-log') {
            document.title = '\u0e1b\u0e23\u0e30\u0e27\u0e31\u0e15\u0e34\u0e01\u0e32\u0e23\u0e43\u0e0a\u0e49\u0e07\u0e32\u0e19 - RMUTTO URL Shortener';
        } else {
            document.title = 'RMUTTO URL Shortener';
        }
    }, [location]);

    return children;
};

const Header = ({ user, onLogout, avatarError, onAvatarError }) => (
    <header className="border-b border-gray-200 bg-white shadow-sm">
        <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">
                <a href="/" className="flex items-center gap-3">
                    <img
                        src="https://arit.rmutto.ac.th/wp-content/uploads/2024/12/logoARIT-th.png"
                        alt="ARIT RMUTTO Logo"
                        className="h-8 w-auto"
                    />
                    <span className="text-xl font-bold text-slate-800">RMUTTO URL Shortener</span>
                </a>
                <div className="flex items-center gap-4">
                    <span className="hidden font-medium text-slate-600 sm:block">{user.displayName}</span>
                    {user.photoURL && !avatarError ? (
                        <img src={user.photoURL} alt={user.displayName} className="h-10 w-10 rounded-full" onError={onAvatarError} />
                    ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500 text-lg font-bold text-white">
                            {user.displayName?.charAt(0).toUpperCase()}
                        </div>
                    )}
                    <button onClick={onLogout} className="rounded-lg bg-red-500 px-4 py-2 font-semibold text-white transition-colors hover:bg-red-600">
                        ออกจากระบบ
                    </button>
                </div>
            </div>
        </nav>
    </header>
);

const ProtectedLayout = ({ user }) => {
    const [avatarError, setAvatarError] = useState(false);

    const handleLogout = async () => {
        await signOut(auth);
    };

    if (!user) return null;

    return (
        <div className="min-h-screen bg-slate-50">
            <Header user={user} onLogout={handleLogout} avatarError={avatarError} onAvatarError={() => setAvatarError(true)} />
            <Outlet context={{ user }} />
        </div>
    );
};

function App() {
    useGoogleFont('Kanit');
    useFavicon('https://arit.rmutto.ac.th/wp-content/uploads/2024/12/logoARIT-th.png');

    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loginError, setLoginError] = useState('');

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            if (currentUser && !currentUser.email.endsWith('@rmutto.ac.th')) {
                signOut(auth);
                setUser(null);
                setLoginError('โปรดใช้บัญชีอีเมลของมหาวิทยาลัย (@rmutto.ac.th) เท่านั้น');
            } else {
                setUser(currentUser);
            }
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const handleLogin = async () => {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ hd: 'rmutto.ac.th' });

        try {
            setLoginError('');
            const result = await signInWithPopup(auth, provider);
            const userEmail = result.user.email;

            if (!userEmail.endsWith('@rmutto.ac.th')) {
                await signOut(auth);
                setLoginError('โปรดใช้บัญชีอีเมลของมหาวิทยาลัย (@rmutto.ac.th) เท่านั้น');
            }
        } catch (error) {
            console.error('Authentication error:', error);
            if (error.code !== 'auth/popup-closed-by-user') {
                setLoginError('เกิดข้อผิดพลาดในการเข้าสู่ระบบ โปรดลองอีกครั้ง');
            }
        }
    };

    if (isLoading) {
        return <LoadingState message="กำลังโหลด..." />;
    }

    return (
        <ToastProvider>
            <Router basename="">
                <AppManager>
                    <Routes>
                        <Route path="/:shortCode" element={<RedirectHandler />} />

                        <Route path="/" element={user ? <ProtectedLayout user={user} /> : <Navigate to="/login" replace />}>
                            <Route index element={<Dashboard />} />
                            <Route path="details/:linkId" element={<LinkDetails />} />
                            <Route path="admin-report" element={<AdminReport />} />
                            <Route path="audit-log" element={<AuditLog />} />
                        </Route>

                        <Route
                            path="/login"
                            element={!user ? <LoginScreen onLogin={handleLogin} loginError={loginError} /> : <Navigate to="/" replace />}
                        />
                    </Routes>
                </AppManager>
            </Router>
        </ToastProvider>
    );
}

export default App;
